import React, { useState, useEffect } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

const DASHBOARD_AUTH_KEY = 'dashboard_last_auth';
const DASHBOARD_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutos

export const DashboardTimer: React.FC = () => {
  const { user } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (user?.rol_usr !== 'ADMIN') return;

    const calculateRemaining = () => {
      const lastAuth = sessionStorage.getItem(DASHBOARD_AUTH_KEY);
      if (!lastAuth) return null;

      const timeElapsed = Date.now() - parseInt(lastAuth, 10);
      const remaining = Math.max(0, DASHBOARD_TIMEOUT_MS - timeElapsed);
      return remaining;
    };

    // Actualización inicial
    setTimeRemaining(calculateRemaining());

    // Actualizar cada segundo
    const intervalId = setInterval(() => {
      const remaining = calculateRemaining();
      setTimeRemaining(remaining);
      
      // Si llega a 0, detenemos el intervalo visual para no gastar recursos inútilmente
      if (remaining !== null && remaining <= 0) {
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [user]);

  if (user?.rol_usr !== 'ADMIN' || timeRemaining === null) {
    return null;
  }

  // Formato mm:ss
  const minutes = Math.floor(timeRemaining / 60000);
  const seconds = Math.floor((timeRemaining % 60000) / 1000);
  
  // Color según tiempo (rojo si quedan < 2 minutos)
  const isUrgent = timeRemaining <= 2 * 60 * 1000;
  const textColor = isUrgent ? 'text-red-600' : 'text-blue-600';
  const bgColor = isUrgent ? 'bg-red-50' : 'bg-blue-50';
  const borderColor = isUrgent ? 'border-red-200' : 'border-blue-200';

  return (
    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full border ${bgColor} ${borderColor} ${textColor} text-sm font-medium shadow-sm transition-colors duration-300`}>
      <ClockIcon className="w-4 h-4" />
      <span>
        {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
      </span>
    </div>
  );
};
